import React, { useEffect, useMemo, useState } from "react";
import { Alert, Empty, Skeleton } from "antd";
import {
  ArrowRightOutlined,
  FilterOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DashboardCard from "./DashboardCard";
import DashTimeseries from "./DashTimeseries";
import FilterModal from "./FilterModal";
import { fetchDash } from "../../features/dashboard/dashboardSlice";
import { fetchWhatsAppDashboard } from "../../features/whatsapp/whatsappSlice";
import { cleanLegendClickStatus } from "../../features/global/globalSlice";
import {
  cashConverter,
  formatDate,
  getDate30DaysAgo,
  getDate7DaysAgo,
  numberWithCommas,
} from "../../utils";
import "./dashboard-account.css";

const RANGE_OPTIONS = [
  { key: "DAY", label: "Day" },
  { key: "WEEK", label: "Week" },
  { key: "MONTH", label: "Month" },
];

const WHATSAPP_RANGE_DAYS = {
  DAY: 1,
  WEEK: 7,
  MONTH: 30,
};

const numericValue = (value) => Number(value || 0);

const buildRangePayload = (rangeKey) => {
  const today = formatDate(new Date());

  if (rangeKey === "WEEK") {
    return {
      msgDateFrom: getDate7DaysAgo(),
      msgDateTo: today,
    };
  }

  if (rangeKey === "MONTH") {
    return {
      msgDateFrom: getDate30DaysAgo(),
      msgDateTo: today,
    };
  }

  return {
    msgCreatedDate: today,
  };
};

const findStatusCount = (items, status) =>
  numericValue(items?.find((item) => item?.msgStatus === status)?.msgCount);

function DashboardAccount() {
  const { dashData, loading } = useSelector((state) => state.dash);
  const { balanceHeader } = useSelector((state) => state.menu);
  const { legendClickStatus } = useSelector((state) => state.global);
  const { dashboardAnalytics, dashboardError, loadingDashboard } = useSelector(
    (state) => state.whatsapp
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [initialLoad, setInitialLoad] = useState(true);
  const [formData, setFormData] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeBtn, setActiveBtn] = useState("DAY");

  const hasFilters = useMemo(
    () => Object.keys(formData || {}).length > 0,
    [formData]
  );

  const smsStatusSummary = dashData?.ststusSummary ?? [];
  const smsTimeSeries = dashData?.msgTimeSeries ?? [];
  const whatsappSummary = dashboardAnalytics?.summary ?? null;
  const whatsappBreakdown = dashboardAnalytics?.statusBreakdown ?? [];
  const topWhatsAppStatuses = whatsappBreakdown.slice(0, 4);

  const smsTotalCount = useMemo(
    () =>
      smsStatusSummary.reduce(
        (total, item) => total + numericValue(item?.msgCount),
        0
      ),
    [smsStatusSummary]
  );

  const smsDeliveredCount = useMemo(
    () => findStatusCount(smsStatusSummary, "DeliveredToTerminal"),
    [smsStatusSummary]
  );

  const smsQueuedCount = useMemo(
    () =>
      findStatusCount(smsStatusSummary, "SENT") +
      findStatusCount(smsStatusSummary, "SENT_RETRIED"),
    [smsStatusSummary]
  );

  const smsIssueCount = useMemo(
    () =>
      smsStatusSummary
        .filter(
          (item) =>
            !["DeliveredToTerminal", "SENT", "SENT_RETRIED"].includes(
              item?.msgStatus
            )
        )
        .reduce((total, item) => total + numericValue(item?.msgCount), 0),
    [smsStatusSummary]
  );

  const smsDeliveryRate = smsTotalCount
    ? Math.round((smsDeliveredCount / smsTotalCount) * 100)
    : 0;

  const buildSmsRequest = () => {
    const request = {
      url: "api/v2/dash",
    };

    if (hasFilters) {
      return {
        ...request,
        msgStatus: formData?.msgStatus,
        msgCreatedDate: formData?.msgCreatedDate,
        msgSubmobileNo: formData?.msgSubmobileNo,
        msgDateFrom: formData?.msgDateFrom,
        msgDateTo: formData?.msgDateTo,
      };
    }

    if (legendClickStatus) {
      return {
        ...request,
        msgStatus: legendClickStatus,
      };
    }

    return {
      ...request,
      ...buildRangePayload(activeBtn),
    };
  };

  const loadDashboardData = async () => {
    await Promise.all([
      dispatch(fetchDash(buildSmsRequest())),
      dispatch(
        fetchWhatsAppDashboard({ days: WHATSAPP_RANGE_DAYS[activeBtn] })
      ),
    ]);
    setInitialLoad(false);
  };

  const handleClearFilter = () => {
    setFormData({});
    setActiveBtn("DAY");
    dispatch(cleanLegendClickStatus());
  };

  useEffect(() => {
    loadDashboardData();

    const intervalId = setInterval(() => {
      loadDashboardData();
    }, 20000);

    return () => clearInterval(intervalId);
  }, [activeBtn, formData, legendClickStatus]);

  const showSmsContent =
    smsStatusSummary.length > 0 || smsTimeSeries.length > 0 || loading;

  return (
    <>
      <div className="dashboard-account-page weiser-soft-scrollbar">
        <section className="dashboard-account-hero">
          <div className="dashboard-account-hero-copy">
            <span className="dashboard-account-eyebrow">Account dashboard</span>
            <h1>{balanceHeader?.accName || "Messaging workspace"}</h1>
            <p>
              Track SMS traffic, account health, and WhatsApp delivery in one
              cleaner view.
            </p>
          </div>

          <div className="dashboard-account-hero-chips">
            <div className="dashboard-account-chip">
              <span>SMS Balance</span>
              <strong>{cashConverter(balanceHeader?.accBalance || 0)}</strong>
            </div>
            <div className="dashboard-account-chip">
              <span>Units</span>
              <strong>{numberWithCommas(numericValue(balanceHeader?.accUnits))}</strong>
            </div>
            <div
              className={`dashboard-account-chip ${
                balanceHeader?.accStatus === "OUT_OF_CREDIT"
                  ? "is-danger"
                  : "is-success"
              }`}
            >
              <span>Account Status</span>
              <strong>{balanceHeader?.accStatus || "ACTIVE"}</strong>
            </div>
          </div>
        </section>

        <section className="dashboard-account-toolbar">
          <div className="dashboard-account-range">
            {RANGE_OPTIONS.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => setActiveBtn(option.key)}
                className={`dashboard-account-range-button ${
                  activeBtn === option.key ? "is-active" : ""
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="dashboard-account-toolbar-actions">
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className={`dashboard-account-action-button ${
                hasFilters ? "is-highlighted" : ""
              }`}
            >
              <FilterOutlined />
              <span>Advanced Filters</span>
            </button>

            <button
              type="button"
              onClick={loadDashboardData}
              className="dashboard-account-action-button"
            >
              <ReloadOutlined />
              <span>Refresh</span>
            </button>

            <button
              type="button"
              onClick={handleClearFilter}
              className="dashboard-account-clear-button"
            >
              Clear filters
            </button>
          </div>
        </section>

        {(hasFilters || legendClickStatus) && (
          <div className="dashboard-account-active-filters">
            {hasFilters && (
              <span className="dashboard-account-filter-pill">
                SMS advanced filters applied
              </span>
            )}
            {legendClickStatus && (
              <span className="dashboard-account-filter-pill">
                Focused status: {legendClickStatus}
              </span>
            )}
          </div>
        )}

        {initialLoad && loading && !dashboardAnalytics ? (
          <Skeleton active />
        ) : (
          <>
            <section className="dashboard-account-overview-grid">
              <article className="dashboard-account-overview-card tone-sms">
                <span className="dashboard-account-card-label">
                  SMS overview
                </span>
                <strong className="dashboard-account-card-total">
                  {numberWithCommas(smsTotalCount)}
                </strong>
                <p>Messages recorded in the selected SMS window.</p>

                <div className="dashboard-account-mini-stats">
                  <div>
                    <span>Delivered</span>
                    <strong>{numberWithCommas(smsDeliveredCount)}</strong>
                  </div>
                  <div>
                    <span>Queued</span>
                    <strong>{numberWithCommas(smsQueuedCount)}</strong>
                  </div>
                  <div>
                    <span>Delivery rate</span>
                    <strong>{smsDeliveryRate}%</strong>
                  </div>
                </div>
              </article>

              <article className="dashboard-account-overview-card tone-whatsapp">
                <span className="dashboard-account-card-label">
                  WhatsApp overview
                </span>
                <strong className="dashboard-account-card-total">
                  {numberWithCommas(numericValue(whatsappSummary?.totalQueued))}
                </strong>
                <p>Outbound WhatsApp messages in the selected WhatsApp window.</p>

                <div className="dashboard-account-mini-stats">
                  <div>
                    <span>Delivered</span>
                    <strong>
                      {numberWithCommas(
                        numericValue(whatsappSummary?.deliveredCount)
                      )}
                    </strong>
                  </div>
                  <div>
                    <span>Read</span>
                    <strong>
                      {numberWithCommas(
                        numericValue(whatsappSummary?.readCount)
                      )}
                    </strong>
                  </div>
                  <div>
                    <span>Failed</span>
                    <strong>
                      {numberWithCommas(
                        numericValue(whatsappSummary?.failedCount)
                      )}
                    </strong>
                  </div>
                </div>

                <button
                  type="button"
                  className="dashboard-account-inline-link"
                  onClick={() => navigate("/whatsapp-dashboard")}
                >
                  Open WhatsApp dashboard
                  <ArrowRightOutlined />
                </button>
              </article>

              <article className="dashboard-account-overview-card tone-account">
                <span className="dashboard-account-card-label">
                  Account health
                </span>
                <strong className="dashboard-account-card-total">
                  {balanceHeader?.accStatus || "ACTIVE"}
                </strong>
                <p>Balance and delivery posture for the currently signed-in account.</p>

                <div className="dashboard-account-mini-stats">
                  <div>
                    <span>Billing balance</span>
                    <strong>{cashConverter(balanceHeader?.accBalance || 0)}</strong>
                  </div>
                  <div>
                    <span>Units available</span>
                    <strong>
                      {numberWithCommas(numericValue(balanceHeader?.accUnits))}
                    </strong>
                  </div>
                  <div>
                    <span>SMS issues</span>
                    <strong>{numberWithCommas(smsIssueCount)}</strong>
                  </div>
                </div>
              </article>
            </section>

            <section className="dashboard-account-panel">
              <div className="dashboard-account-panel-head">
                <div>
                  <h2>SMS status breakdown</h2>
                  <p>
                    A quick scan of how SMS messages are landing across delivery
                    states.
                  </p>
                </div>
              </div>

              {showSmsContent ? (
                <DashboardCard dashData={dashData} />
              ) : (
                <div className="dashboard-account-empty-state">
                  <Empty description="No SMS dashboard data available for this period" />
                </div>
              )}
            </section>

            <div className="dashboard-account-analytics-grid">
              <section className="dashboard-account-panel dashboard-account-panel-span">
                <DashTimeseries initialLoad={initialLoad} />
              </section>

              <section className="dashboard-account-panel">
                <div className="dashboard-account-panel-head">
                  <div>
                    <h2>WhatsApp snapshot</h2>
                    <p>
                      Delivered and read activity from the existing WhatsApp
                      analytics feed.
                    </p>
                  </div>
                </div>

                {loadingDashboard && !dashboardAnalytics ? (
                  <Skeleton active paragraph={{ rows: 6 }} />
                ) : dashboardError ? (
                  <Alert
                    type="warning"
                    showIcon
                    message="WhatsApp metrics are not available right now"
                    description={dashboardError}
                  />
                ) : whatsappSummary ? (
                  <div className="dashboard-account-whatsapp-panel">
                    <div className="dashboard-account-whatsapp-rate">
                      <span>Delivery rate</span>
                      <strong>{whatsappSummary?.deliveryRate ?? 0}%</strong>
                    </div>

                    <div className="dashboard-account-whatsapp-metrics">
                      <div>
                        <span>Delivered</span>
                        <strong>
                          {numberWithCommas(
                            numericValue(whatsappSummary?.deliveredCount)
                          )}
                        </strong>
                      </div>
                      <div>
                        <span>Read</span>
                        <strong>
                          {numberWithCommas(
                            numericValue(whatsappSummary?.readCount)
                          )}
                        </strong>
                      </div>
                      <div>
                        <span>Dead letter</span>
                        <strong>
                          {numberWithCommas(
                            numericValue(whatsappSummary?.deadLetterCount)
                          )}
                        </strong>
                      </div>
                    </div>

                    <div className="dashboard-account-status-list">
                      {topWhatsAppStatuses.length > 0 ? (
                        topWhatsAppStatuses.map((item) => {
                          const width = whatsappSummary?.totalQueued
                            ? Math.max(
                                8,
                                Math.round(
                                  (numericValue(item?.count) /
                                    numericValue(
                                      whatsappSummary?.totalQueued
                                    )) *
                                    100
                                )
                              )
                            : 0;

                          return (
                            <div
                              key={item?.status}
                              className="dashboard-account-status-row"
                            >
                              <div className="dashboard-account-status-copy">
                                <span>{item?.status}</span>
                                <strong>{numberWithCommas(item?.count)}</strong>
                              </div>
                              <div className="dashboard-account-status-bar">
                                <span style={{ width: `${width}%` }} />
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="dashboard-account-empty-state compact">
                          <Empty description="No WhatsApp status data yet" />
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="dashboard-account-empty-state compact">
                    <Empty description="No WhatsApp analytics available yet" />
                  </div>
                )}
              </section>
            </div>
          </>
        )}
      </div>

      <FilterModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        formData={formData}
        setFormData={setFormData}
      />
    </>
  );
}

export default DashboardAccount;
